import {
  Badge,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Skeleton,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import {
  SortableHead,
  TableSkeletonActionsCell,
  TableSkeletonRow,
  redesignTableStyles,
  type SortDirection,
} from '@/shared/ui/data-table';
import { TABLE_COL_WIDTH } from '@/shared/constants';

import { AUTH_TYPE_LABEL_KEY } from './authTypes';
import { RowMenu } from './RowMenu';
import type { McpCatalogRow, McpCatalogSortKey } from './McpCatalogTable.types';

const SKELETON_ROWS = Array.from({ length: 5 }, (_, index) => index);

interface McpCatalogTableProps {
  rows: McpCatalogRow[];
  isLoading: boolean;
  sortBy: McpCatalogSortKey;
  sortDirection: SortDirection;
  onSort: (column: McpCatalogSortKey) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  deleteDisabled: boolean;
}

export function McpCatalogTable({
  rows,
  isLoading,
  sortBy,
  sortDirection,
  onSort,
  onEdit,
  onDelete,
  deleteDisabled,
}: McpCatalogTableProps) {
  const { t } = useTranslation('admin');

  return (
    <Datatable
      aria-label={t('admin.mcpCatalogPage.mcpCatalog')}
      aria-busy={isLoading || undefined}
      className={redesignTableStyles.table}
    >
      <DatatableHeader>
        <DatatableRow>
          <SortableHead
            column="name"
            label={t('admin.mcpCatalogPanel.name')}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <DatatableHead scope="col">{t('admin.mcpCatalogPanel.url')}</DatatableHead>
          <SortableHead
            column="authType"
            label={t('admin.mcpCatalogPanel.auth')}
            className={TABLE_COL_WIDTH.statusBadge}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          {/* The kebab column carries no heading: the icon in each row says what it is. */}
          <DatatableHead scope="col" className={TABLE_COL_WIDTH.actions} />
        </DatatableRow>
      </DatatableHeader>

      <DatatableBody>
        {isLoading
          ? SKELETON_ROWS.map((index) => (
              <TableSkeletonRow key={index} testId="mcp-catalog-skeleton-row">
                <DatatableCell>
                  <Skeleton className="h-4 w-40" />
                </DatatableCell>
                <DatatableCell>
                  <Skeleton className="h-4 w-64" />
                </DatatableCell>
                <DatatableCell>
                  <Skeleton className="h-5 w-24 rounded-full" />
                </DatatableCell>
                <TableSkeletonActionsCell />
              </TableSkeletonRow>
            ))
          : rows.map((row) => (
              <DatatableRow key={row.id} state="default">
                <DatatableCell>
                  <div className="flex flex-col gap-3xs">
                    <span className="text-body-md font-medium">{row.name}</span>
                    {row.description && (
                      <span className="line-clamp-1 text-body-sm text-muted-foreground">
                        {row.description}
                      </span>
                    )}
                  </div>
                </DatatableCell>
                <DatatableCell>
                  <span className="text-body-sm text-muted-foreground">{row.url}</span>
                </DatatableCell>
                <DatatableCell>
                  <Badge variant="secondary" size="sm" roundness="full">
                    {t(AUTH_TYPE_LABEL_KEY[row.authType])}
                  </Badge>
                </DatatableCell>
                <DatatableCell className={TABLE_COL_WIDTH.actions}>
                  <RowMenu
                    onEdit={() => onEdit(row.id)}
                    onDelete={() => onDelete(row.id)}
                    deleteDisabled={deleteDisabled}
                  />
                </DatatableCell>
              </DatatableRow>
            ))}
      </DatatableBody>
    </Datatable>
  );
}

McpCatalogTable.displayName = 'McpCatalogTable';
